import React, { useState, useEffect, useRef } from "react";
import "./TimeAttendance.css";
import { useNavigate } from 'react-router-dom';
import AttendanceHero from "./AttendanceHero";
import TimeWiseCode from "./TimeWiseCode";
import HRCopilotFeatures from './HRCopilotFeatures';
import HRCopilotNewsSection from "./HRCopilotNewsSection";



const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="timeattendance-video-popup-overlay" onClick={onClose}>
      <div className="timeattendance-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="timeattendance-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="timeattendance-video-container">
          <video ref={videoRef} controls autoPlay className="timeattendance-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="timeattendance-video-popup-info">
          <h3>Time & Attendance Platform</h3>
          <p>See how our workforce management platform streamlines time tracking and attendance management</p>
        </div>
      </div>
    </div>
  );
};

const TimeAttendance = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("time-tracking");
  const navigate = useNavigate();
    const handleRecruit = () => navigate('/access-recruit');
  const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-businessman-using-his-digital-tablet-44537-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  const features = [
    {
      icon: "⏰",
      title: "Smart Time Tracking",
      description: "Automated clock-ins, GPS verification, and biometric authentication for accurate time recording"
    },
    {
      icon: "📊",
      title: "Real-time Analytics",
      description: "Live dashboards showing attendance patterns, overtime trends, and workforce utilization"
    },
    {
      icon: "🤖",
      title: "AI-Powered Scheduling",
      description: "Intelligent shift planning with conflict detection and optimal resource allocation"
    },
    {
      icon: "📱",
      title: "Mobile Workforce",
      description: "Mobile app for remote clock-ins, shift swaps, and real-time notifications"
    },
    {
      icon: "🔒",
      title: "Compliance Management",
      description: "Automated compliance tracking for labor laws, breaks, and overtime regulations"
    },
    {
      icon: "💼",
      title: "Payroll Integration",
      description: "Seamless integration with payroll systems for accurate and timely payments"
    }
  ];

  const implementationStages = [
    {
      phase: "Setup & Configuration",
      title: "Quick Deployment & System Setup",
      steps: ["System Configuration", "Employee Data Import", "Policy Setup", "Device Integration"],
      time: "1-2 Days"
    },
    {
      phase: "Training & Onboarding",
      title: "Comprehensive Team Training",
      steps: ["Admin Training", "Employee Onboarding", "Mobile App Setup", "Support System"],
      time: "2-3 Days"
    },
    {
      phase: "Optimization & Scaling",
      title: "Advanced Features & Analytics",
      steps: ["Advanced Reporting", "Custom Workflows", "Integration Setup", "Performance Monitoring"],
      time: "Ongoing"
    }
  ];

  const tabs = [
    {
      id: "time-tracking",
      label: "TIME TRACKING",
      content: {
        icon: "⏱️",
        title: "Accurate Time Capture",
        description: "Eliminate time theft and manual errors with automated clock-ins, biometric verification, and GPS tracking for complete workforce visibility.",
        features: ["BIOMETRIC CLOCK-INS", "GPS VERIFICATION", "AUTOMATED BREAKS", "REAL-TIME MONITORING"],
        secondary: {
          icon: "📈",
          title: "Real-time Attendance Analytics",
          description: "Monitor attendance patterns, identify trends, and make data-driven decisions with comprehensive analytics dashboards."
        },
        buttonText: "EXPLORE TIME TRACKING"
      }
    },
    {
      id: "scheduling",
      label: "SCHEDULING →",
      content: {
        icon: "📅",
        title: "Intelligent Shift Planning",
        description: "Create optimal schedules with AI-powered recommendations, conflict detection, and automated shift assignments.",
        features: ["AUTOMATED SCHEDULING", "SHIFT SWAPS", "COVERAGE OPTIMIZATION", "LABOR FORECASTING"],
        secondary: {
          icon: "🤖",
          title: "AI-Powered Optimization",
          description: "Leverage machine learning to predict staffing needs, optimize labor costs, and ensure perfect coverage."
        },
        buttonText: "EXPLORE SCHEDULING"
      }
    },
    {
      id: "compliance",
      label: "COMPLIANCE",
      content: {
        icon: "⚖️",
        title: "Regulatory Compliance",
        description: "Stay compliant with labor laws, track breaks, monitor overtime, and generate audit-ready reports automatically.",
        features: ["BREAK COMPLIANCE", "OVERTIME TRACKING", "AUDIT REPORTS", "LEGAL UPDATES"],
        secondary: {
          icon: "🛡️",
          title: "Risk Mitigation",
          description: "Reduce compliance risks with automated alerts, policy enforcement, and comprehensive documentation."
        },
        buttonText: "EXPLORE COMPLIANCE"
      }
    },
    {
      id: "analytics",
      label: "ANALYTICS & REPORTING",
      content: {
        icon: "📊",
        title: "Advanced Workforce Analytics",
        description: "Transform attendance data into actionable insights with predictive analytics and custom reporting.",
        features: ["PREDICTIVE ANALYTICS", "CUSTOM REPORTS", "TREND ANALYSIS", "PERFORMANCE METRICS"],
        secondary: {
          icon: "💡",
          title: "Business Intelligence",
          description: "Make informed decisions with real-time dashboards, cost analysis, and workforce optimization insights."
        },
        buttonText: "EXPLORE ANALYTICS"
      }
    }
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="timeattendance-container">
      {/* Scroll Progress Bar */}
      {/* <div className="timeattendance-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="timeattendance-background-animation">
        {/* <div className="timeattendance-floating-shape timeattendance-shape-1"></div>
        <div className="timeattendance-floating-shape timeattendance-shape-2"></div>
        <div className="timeattendance-floating-shape timeattendance-shape-3"></div>
        <div className="timeattendance-floating-shape timeattendance-shape-4"></div>
        <div className="timeattendance-floating-shape timeattendance-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="timeattendance-gradient-orb timeattendance-orb-1"></div>
        <div className="timeattendance-gradient-orb timeattendance-orb-2"></div>
        <div className="timeattendance-gradient-orb timeattendance-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="timeattendance-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="timeattendance-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.08)" />
              <stop offset="50%" stopColor="rgba(139, 92, 246, 0.05)" />
              <stop offset="100%" stopColor="rgba(236, 72, 153, 0.08)" />
            </linearGradient>
            <linearGradient id="timeattendance-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(139, 92, 246, 0.06)" />
              <stop offset="50%" stopColor="rgba(16, 185, 129, 0.04)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0.06)" />
            </linearGradient>
            <pattern id="timeattendance-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#timeattendance-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#timeattendance-gradient1)"
            className="timeattendance-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#timeattendance-gradient2)"
            opacity="0.8"
            className="timeattendance-svg-path-2"
          />
        </svg>
      </div>

<AttendanceHero/>
<TimeWiseCode/>
<HRCopilotFeatures/>
<HRCopilotNewsSection/>
    </div>
  );
};

export default TimeAttendance;